import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { FileDown, Paperclip, PenLine, Send, Trash2, Upload } from "lucide-react";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../api/client";
import * as ritApi from "../../api/moduliRit";
import { LIMITE_ALLEGATO_EMAIL_BYTE, ModuloRIT } from "../../api/moduliRit";
import * as mediaApi from "../../api/media";
import { downloadProtectedFile } from "../../api/files";
import { Button } from "./Button";
import { Input } from "./Input";
import { Modal } from "./Modal";
import { SignaturePad } from "./SignaturePad";
import { InviaReportModal } from "./InviaReportModal";
import { useAuth } from "../../context/AuthContext";

type EntityType = "intervento" | "installation";

interface RapportinoRITProps {
  entityType: EntityType;
  entityId: string;
  /** Usati solo per precompilare un modulo nuovo la prima volta — non toccano il modulo se già esiste. */
  defaultClienteNome?: string | null;
  defaultIndirizzo?: string | null;
}

type FormValues = {
  data_richiesta: string;
  referente_evo: string;
  attivita: string;
  prodotto: string;
  cliente_nome: string;
  indirizzo: string;
  data_appuntamento: string;
  note_info: string;
  svolgimento_attivita: string;
  ditta_esecutrice: string;
  tecnico_nome: string;
  data_completamento: string;
};

function toFormValues(m?: ModuloRIT | null, defaults?: { clienteNome?: string | null; indirizzo?: string | null }): FormValues {
  return {
    data_richiesta: m?.data_richiesta?.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
    referente_evo: m?.referente_evo ?? "ASSISTENZA - tel. 011 372 40 82",
    attivita: m?.attivita ?? "",
    prodotto: m?.prodotto ?? "",
    cliente_nome: m?.cliente_nome ?? defaults?.clienteNome ?? "",
    indirizzo: m?.indirizzo ?? defaults?.indirizzo ?? "",
    data_appuntamento: m?.data_appuntamento?.slice(0, 10) ?? "",
    note_info: m?.note_info ?? "",
    svolgimento_attivita: m?.svolgimento_attivita ?? "",
    ditta_esecutrice: m?.ditta_esecutrice ?? "",
    tecnico_nome: m?.tecnico_nome ?? "",
    data_completamento: m?.data_completamento?.slice(0, 10) ?? "",
  };
}

/** Il peso di un file come si scrive a un essere umano. */
function peso(byte: number): string {
  return byte >= 1024 * 1024 ? `${(byte / 1024 / 1024).toFixed(1)} MB` : `${Math.round(byte / 1024)} KB`;
}

/**
 * Rapportino R.I.T. — digitalizza il modulo cartaceo "Richiesta Intervento
 * Tecnico" di Evo. Il form resta sempre compilabile/modificabile, anche
 * dopo aver già scaricato un PDF in precedenza: non è un documento
 * congelato alla prima generazione, ogni "Scarica PDF" rilegge i dati
 * correnti dal database.
 */
export function RapportinoRIT({ entityType, entityId, defaultClienteNome, defaultIndirizzo }: RapportinoRITProps) {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  /** Pannello di invio per email del rapportino. */
  const [invioAperto, setInvioAperto] = useState(false);
  const [modulo, setModulo] = useState<ModuloRIT | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [firmaAperta, setFirmaAperta] = useState<"tecnico" | "cliente" | null>(null);
  const [isSavingFirma, setIsSavingFirma] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isCaricandoEsterno, setIsCaricandoEsterno] = useState(false);
  const [isAprendoEsterno, setIsAprendoEsterno] = useState(false);
  const fileEsternoRef = useRef<HTMLInputElement>(null);
  /**
   * Se allegare il documento del centro assistenza all'email. Si decide
   * all'apertura del pannello di invio, non qui: dipende dal peso, e il peso
   * cambia se nel frattempo il documento è stato sostituito.
   */
  const [includiEsterno, setIncludiEsterno] = useState(true);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty },
  } = useForm<FormValues>({ defaultValues: toFormValues(null, { clienteNome: defaultClienteNome, indirizzo: defaultIndirizzo }) });

  async function load() {
    setIsLoading(true);
    try {
      const { modulo: m } = await ritApi.getModuloRIT(entityType, entityId);
      setModulo(m);
      reset(toFormValues(m, { clienteNome: defaultClienteNome, indirizzo: defaultIndirizzo }));
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare il rapportino."), "error");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (modulo) {
        const { modulo: updated } = await ritApi.updateModuloRIT(modulo.id, values);
        setModulo(updated);
      } else {
        const { modulo: created } = await ritApi.createModuloRIT(entityType, entityId, values);
        setModulo(created);
      }
      showToast("Rapportino salvato.", "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile salvare il rapportino."), "error");
    }
  });

  async function handleSaveFirma(file: File) {
    if (!firmaAperta) return;
    setIsSavingFirma(true);
    try {
      await mediaApi.uploadMedia(entityType, entityId, file, firmaAperta === "tecnico" ? "firma_tecnico" : "firma_cliente");
      showToast("Firma salvata.", "success");
      setFirmaAperta(null);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile salvare la firma."), "error");
    } finally {
      setIsSavingFirma(false);
    }
  }

  async function handleDownload() {
    if (!modulo) return;
    setIsDownloading(true);
    try {
      await ritApi.downloadModuloRITPdf(modulo.id, `rapportino-rit-${modulo.id.slice(0, 8)}.pdf`);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile generare il PDF."), "error");
    } finally {
      setIsDownloading(false);
    }
  }

  async function handleCaricaEsterno(e: React.ChangeEvent<HTMLInputElement>) {
    const scelti = Array.from(e.target.files ?? []);
    // L'input si azzera SEMPRE, anche quando il caricamento fallisce: senza,
    // riscegliere lo stesso file non emette `change` e il secondo tentativo
    // sembra non fare niente.
    e.target.value = "";
    if (scelti.length === 0) return;

    setIsCaricandoEsterno(true);
    try {
      const { modulo: aggiornato } = await ritApi.uploadRapportinoEsterno(entityType, entityId, scelti);
      setModulo(aggiornato);
      // Il modulo può essere stato creato adesso dal server (si carica il
      // documento anche prima di aver compilato il R.I.T.): senza questo reset
      // il form resterebbe sui valori di prima e "Salva" apparirebbe spento.
      reset(toFormValues(aggiornato, { clienteNome: defaultClienteNome, indirizzo: defaultIndirizzo }));
      showToast("Rapportino del centro assistenza caricato.", "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare il documento."), "error");
    } finally {
      setIsCaricandoEsterno(false);
    }
  }

  async function handleApriEsterno() {
    if (!modulo?.file_url_esterno) return;
    setIsAprendoEsterno(true);
    try {
      await downloadProtectedFile(modulo.file_url_esterno, modulo.nome_file_esterno ?? "rapportino-centro-assistenza.pdf");
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile aprire il documento."), "error");
    } finally {
      setIsAprendoEsterno(false);
    }
  }

  async function handleEliminaEsterno() {
    if (!modulo) return;
    // Una conferma e non un annulla: il file è l'unica copia di un documento
    // che l'app non sa rigenerare, e chi lo cancella per sbaglio deve
    // richiederlo al centro assistenza.
    if (!window.confirm("Eliminare il rapportino del centro assistenza? Il file viene tolto dal server e non è recuperabile.")) {
      return;
    }
    try {
      const { modulo: aggiornato } = await ritApi.eliminaRapportinoEsterno(modulo.id);
      setModulo(aggiornato);
      showToast("Documento eliminato.", "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile eliminare il documento."), "error");
    }
  }

  if (isLoading) return <p className="text-xs text-ink/40">Caricamento…</p>;

  return (
    <div>
      <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Data richiesta" type="date" {...register("data_richiesta")} />
          <Input label="Data appuntamento" type="date" {...register("data_appuntamento")} />
        </div>
        <Input label="Referente Evo" {...register("referente_evo")} />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Attività" {...register("attivita")} />
          <Input label="Prodotto" {...register("prodotto")} />
        </div>
        <Input label="Cliente" {...register("cliente_nome")} />
        <Input label="Indirizzo" {...register("indirizzo")} />

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">Note/Info</label>
          <textarea rows={2} className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink" {...register("note_info")} />
        </div>

        <div className="border-t border-line pt-3">
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ink/50">Compilazione a cura del tecnico</p>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink">Svolgimento attività</label>
            <textarea
              rows={4}
              className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink"
              {...register("svolgimento_attivita")}
            />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Input label="Ditta esecutrice" {...register("ditta_esecutrice")} />
            <Input label="Tecnico Sig." {...register("tecnico_nome")} />
          </div>
          <div className="mt-3">
            <Input label="Data completamento" type="date" {...register("data_completamento")} />
          </div>
        </div>

        <div className="mt-2 flex justify-end">
          <Button type="submit" size="sm" loading={isSubmitting} disabled={!isDirty}>
            {modulo ? "Salva modifiche" : "Crea rapportino"}
          </Button>
        </div>
      </form>

      {/* ------------------------------------------------------------------
          Rapportino del centro assistenza — il documento che compila chi va sul
          posto. Sta qui e non fra gli allegati dell'intervento perché non è un
          allegato: finisce DENTRO il PDF del rapportino, in coda al modulo.

          Il riquadro c'è anche quando il modulo R.I.T. non esiste ancora: il
          documento arriva dal campo, spesso prima che qualcuno in ufficio abbia
          compilato il modulo, e il server crea quello che manca.
          ------------------------------------------------------------------ */}
      <div className="mt-4 border-t border-line pt-4">
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ink/50">Rapportino del centro assistenza</p>

        {modulo?.file_url_esterno ? (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line px-3 py-2">
            <Paperclip className="h-4 w-4 shrink-0 text-ink/40" aria-hidden="true" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm text-ink">{modulo.nome_file_esterno ?? "Documento"}</span>
              <span className="block text-xs text-ink/50">
                {modulo.size_bytes_esterno !== null && `${peso(modulo.size_bytes_esterno)} · `}
                {modulo.mime_type_esterno && modulo.mime_type_esterno !== "application/pdf"
                  ? "convertito in PDF dalle foto"
                  : "PDF"}
                {modulo.caricato_il && ` · caricato il ${new Date(modulo.caricato_il).toLocaleDateString("it-IT")}`}
              </span>
            </span>
            <Button type="button" size="sm" variant="ghost" loading={isAprendoEsterno} onClick={handleApriEsterno}>
              <FileDown className="h-4 w-4" aria-hidden="true" />
              Apri
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              loading={isCaricandoEsterno}
              onClick={() => fileEsternoRef.current?.click()}
            >
              <Upload className="h-4 w-4" aria-hidden="true" />
              Sostituisci
            </Button>
            {/* Sostituire basta a rimediare a un file sbagliato, e lo può fare
                chiunque compili il rapportino. Azzerarlo è un'altra cosa e ha
                un permesso suo (solo admin). */}
            {hasPermission("rit:documento_delete") && (
              <Button type="button" size="sm" variant="ghost" onClick={handleEliminaEsterno}>
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                Elimina
              </Button>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-primary/5 px-3 py-2">
            <p className="text-xs text-ink/70">
              Se il centro assistenza ha compilato un suo rapportino, caricalo qui: verrà unito in coda al PDF del R.I.T.
              quando lo scarichi o lo spedisci.
            </p>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              loading={isCaricandoEsterno}
              onClick={() => fileEsternoRef.current?.click()}
            >
              <Upload className="h-4 w-4" aria-hidden="true" />
              Carica
            </Button>
          </div>
        )}

        {/* `multiple` per il caso normale: il tecnico fotografa il foglio, e se
            il rapportino è di due pagine sono due scatti dello stesso documento.
            HEIC non è nell'`accept` perché il server non lo legge — vale come
            filtro nel dialogo di sistema, non come controllo (quello è di là). */}
        <input
          ref={fileEsternoRef}
          type="file"
          multiple
          accept="application/pdf,image/jpeg,image/png"
          className="hidden"
          onChange={handleCaricaEsterno}
        />
      </div>

      {modulo && (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-line pt-4">
          <Button type="button" size="sm" variant="secondary" onClick={() => setFirmaAperta("tecnico")}>
            <PenLine className="h-4 w-4" aria-hidden="true" />
            Firma tecnico
          </Button>
          <Button type="button" size="sm" variant="secondary" onClick={() => setFirmaAperta("cliente")}>
            <PenLine className="h-4 w-4" aria-hidden="true" />
            Firma cliente
          </Button>
          <Button type="button" size="sm" loading={isDownloading} onClick={handleDownload}>
            <FileDown className="h-4 w-4" aria-hidden="true" />
            Scarica PDF
          </Button>
          {/* Solo con `report:invia`: leggere e stampare un rapportino è
              un'altra cosa dallo spedirlo a un indirizzo esterno. */}
          {hasPermission("report:invia") && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => {
                // Spuntata di default solo se il documento ci sta nell'email:
                // sopra i 3 MB Graph rifiuta l'allegato, e proporre un invio
                // che fallisce è peggio che proporne uno parziale annunciato.
                setIncludiEsterno(
                  !modulo.file_url_esterno || (modulo.size_bytes_esterno ?? 0) < LIMITE_ALLEGATO_EMAIL_BYTE
                );
                setInvioAperto(true);
              }}
            >
              <Send className="h-4 w-4" aria-hidden="true" />
              Invia
            </Button>
          )}
        </div>
      )}

      {modulo && (
        <InviaReportModal
          open={invioAperto}
          onClose={() => setInvioAperto(false)}
          titolo="Invia il rapportino R.I.T."
          nomeAllegato={`rapportino-rit-${modulo.id.slice(0, 8)}.pdf`}
          caricaDestinatari={() => ritApi.destinatariPropostiModuloRIT(modulo.id)}
          invia={(destinatari, nota) => ritApi.inviaModuloRIT(modulo.id, destinatari, nota, includiEsterno)}
        >
          {modulo.file_url_esterno && (
            <div className="flex flex-col gap-1.5">
              <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-line px-3 py-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-black/20"
                  checked={includiEsterno}
                  onChange={(e) => setIncludiEsterno(e.target.checked)}
                />
                <span>
                  <span className="block text-ink">Includi il rapportino del centro assistenza</span>
                  <span className="block text-xs text-ink/50">
                    {modulo.nome_file_esterno ?? "Documento"}
                    {modulo.size_bytes_esterno !== null && ` · ${peso(modulo.size_bytes_esterno)}`}
                  </span>
                </span>
              </label>
              {/* Non è un divieto, è quello che succederà: il tetto lo fa
                  rispettare Graph, e provarci lo stesso restituisce un errore
                  per ogni destinatario invece di un invio. */}
              {(modulo.size_bytes_esterno ?? 0) >= LIMITE_ALLEGATO_EMAIL_BYTE && includiEsterno && (
                <p className="text-xs font-semibold text-accent">
                  Il documento supera i 3 MB ammessi per un allegato: l'invio verrà rifiutato. Togli la spunta per
                  spedire il solo modulo R.I.T., e fai avere il documento completo scaricandolo da qui.
                </p>
              )}
            </div>
          )}
        </InviaReportModal>
      )}

      <Modal
        open={firmaAperta !== null}
        onClose={() => setFirmaAperta(null)}
        title={firmaAperta === "tecnico" ? "Firma del tecnico" : "Firma del cliente"}
        size="sm"
      >
        <SignaturePad onCancel={() => setFirmaAperta(null)} onConfirm={handleSaveFirma} />
        {isSavingFirma && <p className="mt-2 text-center text-xs text-ink/40">Salvataggio…</p>}
      </Modal>
    </div>
  );
}
